import { cn } from '@/lib/cn';
import type { SelectionAiActionId } from '@/lib/selection-ai-prompts';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { ChevronRight, WandSparkles } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { V2_REWRITE_SUBMENU } from './selection-ai-actions.config';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';
import { ACTION_ROW_CLASS } from './selection-ai-actions.utils';

export const SelectionRewriteSubmenu = ({
  onActionClick,
}: {
  onActionClick: (actionId: SelectionAiActionId) => void;
}) => {
  const intl = useIntl();
  const [isRewriteMenuOpen, setIsRewriteMenuOpen] = useState(false);

  return (
    <Dropdown.Root open={isRewriteMenuOpen} onOpenChange={setIsRewriteMenuOpen}>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          className={cn(
            ACTION_ROW_CLASS,
            // Keep the row highlighted while its submenu is open (focus
            // moves into the submenu, so hover/focus alone would drop it)
            // — matches the "More options" submenu trigger.
            'data-[state=open]:bg-surface-hover data-[state=open]:text-text-primary'
          )}
          data-testid="selection-ai-rewrite-submenu-trigger"
        >
          <WandSparkles size={14} className="shrink-0 text-text-secondary" />
          <span className="flex-1">
            {intl.formatMessage(selectionAiActionsMessages.rewrite)}
          </span>
          <ChevronRight size={14} className="shrink-0 text-text-secondary" />
        </button>
      </Dropdown.Trigger>
      {/* No Portal: the flyout renders inline in the menu so Radix
          anchors it to the trigger's rect and it pops out beside the
          parent row (a body Portal in this tippy-hosted menu loses the
          anchor and snaps to the viewport's top-left). The whole
          AI-actions bubble menu is appended to <body> (see
          `text-menu-base` appendTo), so this inline flyout still paints
          above the side panel. `avoidCollisions` (default) flips it to
          the left when there's no room on the right. Styling mirrors the
          design-system DropdownMenuContent / DropdownMenuItem so padding,
          gap, ring and shadow match the "More options" submenu. */}
      <Dropdown.Content
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        onMouseDown={(e) => e.preventDefault()}
        className="z-[60] flex min-w-32 flex-col rounded-lg border-0 bg-surface-elevated p-1 text-text-primary shadow-popover ring-1 ring-border"
      >
        {V2_REWRITE_SUBMENU.map(({ id, icon: Icon, message }) => (
          <Dropdown.Item key={id} asChild>
            <button
              type="button"
              onClick={() => {
                setIsRewriteMenuOpen(false);
                onActionClick(id);
              }}
              className={ACTION_ROW_CLASS}
              data-testid={`selection-ai-action-${id}`}
            >
              <Icon className="shrink-0 text-text-secondary" />
              {intl.formatMessage(message)}
            </button>
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};
